import { Grid, Paper, Tabs, Text } from '@mantine/core';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useMyForm } from '../../../../../../react-commons/hooks/useMyForm';
import { QueryBaseComponent } from '../../../../../../react-commons/query/QueryBaseComponent';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import {
    defaultProjectUserGroupResource,
    ProjectUserGroupMutationResourceSchema,
    ProjectUserGroupPropertyValidation,
    useProjectUserGroupActions,
} from '../../../../resource/resources/persistables/user/projectUserGroupResource';
import type { ProjectUserGroupRoleAssignmentResource } from '../../../../resource/resources/persistables/user/projectUserGroupRoleAssignmentResource';
import { PropertyInputs } from '../../../reusable-components/base/property-bases/PropertyInputs';
import { Form_EditableViewWrapper } from '../../../reusable-components/form-components/Form_EditableViewWrapper';
import { ResourceValidationTitleBar } from '../../../reusable-components/validation/ResourceValidationTitleBar';
import { selectReadOnly } from '../../../util/useSelectorFunctions';
import { ProjectUserGroupRolesTabContent } from './ProjectUserGroupRolesTabContent';
import { ProjectUserGroupUsersTabContent } from './ProjectUserGroupUsersTabContent';

export const ProjectUserGroupView = ({
    id,
    projectUserGroupRoleAssignments,
    parentId,
}: {
    id: string | undefined;
    projectUserGroupRoleAssignments: Array<ProjectUserGroupRoleAssignmentResource>;
    parentId: string;
}) => {
    const [tabValue, setTabValue] = React.useState<string | null>('projectUserGroups');
    const readOnly = useSelector(selectReadOnly);

    const projectUserGroupActions = useProjectUserGroupActions();

    const projectUserGroupQuery = projectUserGroupActions.useGetResource(id);
    const updateProjectUserGroup = projectUserGroupActions.useUpdateResource();
    const validateProjectUserGroup = projectUserGroupActions.useValidateResource(id);

    const roleAssignments = projectUserGroupRoleAssignments.filter((value) => value.projectUserGroup === id);

    const { form, formNames, onSubmit } = useMyForm({
        schema: ProjectUserGroupMutationResourceSchema,
        defaultValues: defaultProjectUserGroupResource,
        submitHandler: async (data) => {
            await updateProjectUserGroup.mutateAsync({
                resource: data,
                id: data.id,
            });
        },
        reactiveValues: projectUserGroupQuery.data,
    });

    return (
        <QueryBaseComponent name="Project User Group" query={projectUserGroupQuery}>
            <Form_EditableViewWrapper form={form} onSubmit={onSubmit}>
                <ResourceValidationTitleBar
                    resourceValidationResult={validateProjectUserGroup.data}
                    resourceType={ResourceType.PROJECT_USER_GROUP}
                >
                    <Text c="dimmed">Project User Group</Text>
                </ResourceValidationTitleBar>
                <Grid>
                    <Grid.Col span={12}>
                        <PropertyInputs.Name
                            name={formNames.name}
                            control={form.control}
                            readOnly={readOnly}
                            propertyValidationInformation={{
                                resourceId: id,
                                resourceType: ResourceType.PROJECT_USER_GROUP,
                                propertyIdentifier: ProjectUserGroupPropertyValidation.NAME,
                            }}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <PropertyInputs.MultilineString
                            name={formNames.description}
                            control={form.control}
                            nullable={false}
                            readOnly={readOnly}
                            propertyValidationInformation={{
                                resourceId: id,
                                resourceType: ResourceType.PROJECT_USER_GROUP,
                                propertyIdentifier: ProjectUserGroupPropertyValidation.DESCRIPTION,
                            }}
                        />
                    </Grid.Col>
                    <Grid.Col span={12}>
                        <Paper variant={'outlined'}>
                            <Tabs value={tabValue} onChange={setTabValue}>
                                <Tabs.List>
                                    <Tabs.Tab value="projectUserGroups">Project User Groups</Tabs.Tab>
                                    <Tabs.Tab value="projectUserRoles">Project User Roles</Tabs.Tab>
                                </Tabs.List>
                                <Tabs.Panel value={'projectUserGroups'}>
                                    <ProjectUserGroupUsersTabContent
                                        form={form}
                                        formNames={formNames}
                                        id={id}
                                        parentId={parentId}
                                    />
                                </Tabs.Panel>
                                <Tabs.Panel value={'projectUserRoles'}>
                                    <ProjectUserGroupRolesTabContent
                                        id={id}
                                        projectUserGroupRoleAssignment={roleAssignments.find(
                                            (value) => value.projectUserGroup === id
                                        )}
                                        parentId={parentId}
                                    />
                                </Tabs.Panel>
                            </Tabs>
                        </Paper>
                    </Grid.Col>
                </Grid>
            </Form_EditableViewWrapper>
        </QueryBaseComponent>
    );
};
